/**
 * FNV-1a 32-bit hash over a byte buffer.
 * Returns a fixed-length 8-character lowercase hex string.
 * Deterministic, synchronous, no DOM.
 */
export function fnv1a32(data: Uint8Array | Uint8ClampedArray): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    hash ^= data[i]!;
    // Multiply by FNV prime 0x01000193, keeping only the low 32 bits.
    hash = Math.imul(hash, 0x01000193);
  }
  // Convert to unsigned 32-bit then to 8-char hex.
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Hash an ImageData including its dimensions.
 * A 1×4 image and a 2×2 image with identical bytes must produce
 * different hashes, so width and height are mixed in before the pixel
 * data using the same FNV-1a state.
 */
export function hashImageData(image: ImageData): string {
  // Encode width and height as 4 bytes each, little-endian.
  const dimBuf = new Uint8Array(8);
  const view = new DataView(dimBuf.buffer);
  view.setUint32(0, image.width, /* littleEndian */ true);
  view.setUint32(4, image.height, /* littleEndian */ true);

  // Run FNV-1a over dims, then continue over pixel data.
  let hash = 0x811c9dc5;
  for (let i = 0; i < dimBuf.length; i++) {
    hash ^= dimBuf[i]!;
    hash = Math.imul(hash, 0x01000193);
  }
  for (let i = 0; i < image.data.length; i++) {
    hash ^= image.data[i]!;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
